// src/pages/review-requests/ReviewRequests.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Pagination,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  Divider,
  Checkbox,
  LinearProgress,
  
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SortIcon from "@mui/icons-material/Sort";
import LaunchIcon from "@mui/icons-material/Launch";
import { baseUrl } from "../../constants";

dayjs.extend(relativeTime);

interface ReviewRequest {
  id: number;
  approved: boolean;
  answered: boolean;
  userId: number;
  createdById: number;
  imageUrl: string | null;
  createdByName: string;
  taskId: number;
  taskTitle: string;
  createdAt?: string;
}

interface ReviewRequestResponse {
  pagination: { total: number };
  data: ReviewRequest[];
}

type Sort = "newest" | "oldest";
type Filter = "all" | "pending" | "accepted" | "rejected";

const LS_KEY = "reviewRequests:prefs";

const statusChip = (req: ReviewRequest) => {
  if (!req.answered) return <Chip size="small" color="warning" label="Pending" />;
  if (req.approved) return <Chip size="small" color="success" label="Accepted" />;
  return <Chip size="small" color="error" label="Rejected" />;
};

const ReviewRequests: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // data
  const [rows, setRows] = useState<ReviewRequest[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);

  // ui state
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<Sort>("newest");
  const [filter, setFilter] = useState<Filter>("all");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "success" });

  // selection for bulk actions
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loadingButtons, setLoadingButtons] = useState<Record<number, { approve?: boolean; decline?: boolean }>>({});

  const searchRef = useRef<HTMLInputElement | null>(null);

  // restore prefs
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p.limit) setLimit(p.limit);
        if (p.sort) setSort(p.sort);
        if (p.filter) setFilter(p.filter);
      }
    } catch {}
  }, []);

  // persist prefs
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({ limit, sort, filter }));
  }, [limit, sort, filter]);

  const fetchReviewRequests = useCallback(
    async (p = page, l = limit) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get<ReviewRequestResponse>(
          `${baseUrl}/review-requests?page=${p}&limit=${l}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRows(res.data.data ?? []);
        setTotalPages(Math.max(1, Math.ceil((res.data.pagination?.total ?? 0) / l)));
      } catch (e) {
        console.error("Error fetching review requests:", e);
        setSnackbar({ open: true, severity: "error", message: "Failed to load review requests." });
      } finally {
        setLoading(false);
      }
    },
    [page, limit]
  );

  useEffect(() => {
    void fetchReviewRequests(page, limit);
  }, [page, limit, fetchReviewRequests]);

  // filtering, search, sorting on-page (server not filtering by search)
  const visible = useMemo(() => {
    const lower = search.trim().toLowerCase();
    let list = rows.filter((r) => {
      if (filter === "pending" && r.answered) return false;
      if (filter === "accepted" && (!r.answered || !r.approved)) return false;
      if (filter === "rejected" && (!r.answered || r.approved)) return false;
      if (!lower) return true;
      const hay = `${r.createdByName ?? ""} ${r.taskTitle ?? ""}`.toLowerCase();
      return hay.includes(lower);
    });

    list = list.sort((a, b) => {
      const ta = new Date(a.createdAt ?? 0).getTime();
      const tb = new Date(b.createdAt ?? 0).getTime();
      return sort === "newest" ? tb - ta : ta - tb;
    });

    return list;
  }, [rows, search, filter, sort]);

  const resetSelection = () => setSelected(new Set());

  // actions
  const handleAction = async (id: number, action: "approve" | "decline") => {
    setLoadingButtons((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [action]: true } }));
    try {
      const token = localStorage.getItem("accessToken");
      await axios.patch(
        `${baseUrl}/review-requests/${id}`,
        { approve: action === "approve" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({
        open: true,
        severity: "success",
        message: action === "approve" ? "Request accepted." : "Request rejected.",
      });
      await fetchReviewRequests(page, limit);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (e) {
      setSnackbar({
        open: true,
        severity: "error",
        message: `Failed to ${action} request.`,
      });
    } finally {
      setLoadingButtons((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [action]: false } }));
    }
  };

  const bulkAct = async (action: "approve" | "decline") => {
    if (!selected.size) return;
    setBusy(true);
    try {
      const token = localStorage.getItem("accessToken");
      await Promise.all(
        [...selected].map((id) =>
          axios.patch(
            `${baseUrl}/review-requests/${id}`,
            { approve: action === "approve" },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      setSnackbar({
        open: true,
        severity: "success",
        message: action === "approve" ? "Selected requests accepted." : "Selected requests rejected.",
      });
      await fetchReviewRequests(page, limit);
      resetSelection();
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: "Bulk action failed." });
    } finally {
      setBusy(false);
    }
  };

  // keyboard: ⌘/Ctrl+K focus search, ⌘/Ctrl+A select all on page, ⌘/Ctrl+R refresh
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const meta = isMac ? e.metaKey : e.ctrlKey;
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (meta && e.key.toLowerCase() === "a") {
        e.preventDefault();
        setSelected(new Set(visible.map((r) => r.id)));
      }
      if (meta && e.key.toLowerCase() === "r") {
        e.preventDefault();
        void fetchReviewRequests(1, limit);
        setPage(1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [visible, limit, fetchReviewRequests]);

  const allSelectedOnPage = visible.length > 0 && visible.every((r) => selected.has(r.id));
  const toggleSelectAll = () => {
    if (allSelectedOnPage) resetSelection();
    else setSelected(new Set(visible.map((r) => r.id)));
  };

  const dark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
        display: "flex",
        justifyContent: "center",
      }}
    >


      <Box sx={{ width: "100%", maxWidth: 1000 }}>
        {/* Controls */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            position: "sticky",
            top: 88,
            zIndex: 10,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            backdropFilter: "blur(6px)",
          }}
        >
          {loading && <LinearProgress sx={{ mb: 1 }} />}
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography variant="h6" fontWeight={800} sx={{ flex: 1, minWidth: 180 }}>
              Review Requests
            </Typography>

            <TextField
              inputRef={searchRef}
              size="small"
              placeholder="Search by requester or task…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: "100%", sm: 280 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value as Filter)}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start" sx={{ pl: 1 }}>
                    <FilterAltIcon fontSize="small" />
                  </InputAdornment>
                }
                renderValue={(v) => {
                  const label =
                    v === "all" ? "All statuses" : v === "pending" ? "Pending" : v === "accepted" ? "Accepted" : "Rejected";
                  return <Box sx={{ ml: 1 }}>{label}</Box>;
                }}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select
                value={sort}
                onChange={(e) => setSort(e.target.value as Sort)}
                startAdornment={
                  <InputAdornment position="start" sx={{ pl: 1 }}>
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                }
                renderValue={(v) => <Box sx={{ ml: 1 }}>{v === "newest" ? "Newest first" : "Oldest first"}</Box>}
              >
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                <MenuItem value={10}>10 / page</MenuItem>
                <MenuItem value={20}>20 / page</MenuItem>
                <MenuItem value={50}>50 / page</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title="Refresh (Ctrl/⌘+R)">
              <span>
                <IconButton onClick={() => fetchReviewRequests(1, limit)} disabled={loading}>
                  {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Stack direction="row" spacing={1} mt={1} alignItems="center" flexWrap="wrap">
            <Checkbox
              checked={allSelectedOnPage}
              indeterminate={selected.size > 0 && !allSelectedOnPage}
              onChange={toggleSelectAll}
            />
            <Typography variant="body2" color="text.secondary">
              {selected.size ? `${selected.size} selected` : "Select all on page"}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Tooltip title="Accept selected">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<DoneAllIcon />}
                  onClick={() => bulkAct("approve")}
                  disabled={!selected.size || busy}
                  sx={{ minWidth: 160 }}
                >
                  Accept Selected
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Reject selected">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => bulkAct("decline")}
                  disabled={!selected.size || busy}
                  sx={{ minWidth: 160 }}
                >
                  Reject Selected
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Paper>

        {/* Content */}
        <Paper
          elevation={0}
          sx={{
            p: 0,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
            bgcolor: theme.palette.background.paper,
          }}
        >
          {loading ? (
            <Box sx={{ py: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : visible.length === 0 ? (
            <Box sx={{ py: 10, textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                No review requests match your filters.
              </Typography>
              <Typography color="text.secondary">Try changing the filters or search.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {/* header row (non-sticky to avoid overlap issues) */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "grid",
                  gridTemplateColumns: "48px auto 1fr auto",
                  gap: 1.5,
                  alignItems: "center",
                  bgcolor: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Checkbox
                  checked={allSelectedOnPage}
                  indeterminate={selected.size > 0 && !allSelectedOnPage}
                  onChange={toggleSelectAll}
                />
                <Typography variant="caption" color="text.secondary">
                  Requester
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Details
                </Typography>
                <Typography variant="caption" color="text.secondary" textAlign="right">
                  Actions
                </Typography>
              </Box>

              {visible.map((req) => {
                const isSelected = selected.has(req.id);
                return (
                  <ListItem
                    key={req.id}
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      display: "grid",
                      gridTemplateColumns: "48px auto 1fr auto",
                      gap: 1.5,
                      alignItems: "flex-start",
                      "&:hover": {
                        bgcolor: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                      },
                    }}
                  >
                    {/* Select */}
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(req.id);
                        else next.delete(req.id);
                        setSelected(next);
                      }}
                    />

                    {/* Requester */}
                    <ListItemAvatar sx={{ minWidth: 0 }}>
                      <Avatar src={req.imageUrl || undefined} alt={req.createdByName} />
                    </ListItemAvatar>

                    {/* Details */}
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography fontWeight={700}>{req.createdByName}</Typography>
                          {statusChip(req)}
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<LaunchIcon fontSize="small" />}
                            label="Open task"
                            onClick={() => navigate(`/task/${req.taskId}`)}
                          />
                        </Stack>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mt: 0.5,
                            }}
                            title={req.taskTitle}
                          >
                            {req.taskTitle}
                          </Typography>
                          {req.createdAt && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ mt: 0.25, display: "block" }}
                              title={new Date(req.createdAt).toLocaleString()}
                            >
                              {dayjs(req.createdAt).fromNow()}
                            </Typography>
                          )}
                        </>
                      }
                    />

                    {/* Actions */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      {!req.answered ? (
                        <>
                          <Tooltip title="Accept">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<DoneIcon />}
                                onClick={() => handleAction(req.id, "approve")}
                                disabled={!!loadingButtons[req.id]?.approve}
                              >
                                {loadingButtons[req.id]?.approve ? (
                                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                                ) : (
                                  "Accept"
                                )}
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CloseIcon />}
                                onClick={() => handleAction(req.id, "decline")}
                                disabled={!!loadingButtons[req.id]?.decline}
                              >
                                {loadingButtons[req.id]?.decline ? (
                                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                                ) : (
                                  "Reject"
                                )}
                              </Button>
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        <Chip
                          size="small"
                          color={req.approved ? "success" : "error"}
                          label={req.approved ? "Accepted" : "Rejected"}
                        />
                      )}
                    </Stack>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="center" pb={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => {
                  setPage(v);
                  resetSelection();
                }}
                color="primary"
                shape="rounded"
              />
            </Box>
          </>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewRequests;
